import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import PaywallRedirect from './components/PaywallRedirect';
import LoadingSpinner from './components/LoadingSpinner';
import { loadTrackingScripts } from './utils/loadTrackingScripts';

// Pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const RecordingPage = lazy(() => import('./pages/RecordingPage'));
const RecordingDetailPage = lazy(() => import('./pages/RecordingDetailPage'));
const YouTubeDetailPage = lazy(() => import('./pages/YouTubeDetailPage'));
const VideoDetailPage = lazy(() => import('./pages/VideoDetailPage'));
const NotesPage = lazy(() => import('./pages/NotesPage'));
const YouTubeProcessorPage = lazy(() => import('./pages/YouTubeProcessorPage'));
const PDFProcessorPage = lazy(() => import('./pages/PDFProcessorPage'));
const PDFDetailPage = lazy(() => import('./pages/PDFDetailPage'));
const PhotoTutorPage = lazy(() => import('./pages/PhotoTutorPage'));
const ProblemDetailPage = lazy(() => import('./pages/ProblemDetailPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const QuizSetupPage = lazy(() => import('./pages/QuizSetupPage'));
const QuizPage = lazy(() => import('./pages/QuizPage'));
const ProcessingPage = lazy(() => import('./pages/ProcessingPage'));
const PaywallPage = lazy(() => import('./pages/PaywallPage'));
const UploadAudioPage = lazy(() => import('./pages/UploadAudioPage'));
const UploadVideoPage = lazy(() => import('./pages/UploadVideoPage'));
const ChatTutorPage = lazy(() => import('./pages/ChatTutorPage'));

function App() {
  useEffect(() => {
    const scheduleLoad = () => {
      loadTrackingScripts();
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(scheduleLoad, { timeout: 3000 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(scheduleLoad, 1500);
    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <AuthProvider>
        <SubscriptionProvider>
          <Router>
            <div className="App">
              <PaywallRedirect />
              <Suspense fallback={<LoadingSpinner text="Loading..." />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  
                  {/* Processing route (no layout) */}
                  <Route 
                    path="/upload-audio" 
                    element={
                      <ProtectedRoute requireUsage={true} requireSubscription={false}>
                        <Layout>
                          <UploadAudioPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/upload-video" 
                    element={
                      <ProtectedRoute requireUsage={true} requireSubscription={false}>
                        <Layout>
                          <UploadVideoPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/processing/:id" 
                    element={
                      <ProtectedRoute requireSubscription={false}>
                        <ProcessingPage />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Protected routes */}
                  <Route 
                    path="/" 
                    element={
                      <ProtectedRoute requireSubscription={false}>
                        <Layout>
                          <DashboardPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/record" 
                    element={
                      <ProtectedRoute requireUsage={true} requireSubscription={false}>
                        <Layout>
                          <RecordingPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/recording/:id" 
                    element={
                      <ProtectedRoute requireSubscription={false}>
                        <Layout>
                          <RecordingDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/notes" 
                    element={
                      <ProtectedRoute requireSubscription={false}>
                        <Layout>
                          <NotesPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/youtube" 
                    element={
                      <ProtectedRoute requireUsage={true} requireSubscription={false}>
                        <Layout>
                          <YouTubeProcessorPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/pdf-processor" 
                    element={
                      <ProtectedRoute requireUsage={true} requireSubscription={false}>
                        <Layout>
                          <PDFProcessorPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/pdf/:id" 
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <PDFDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/youtube/:id" 
                    element={
                      <ProtectedRoute requireSubscription={false}>
                        <Layout>
                          <YouTubeDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/video/:id" 
                    element={
                      <ProtectedRoute requireSubscription={false}>
                        <Layout>
                          <VideoDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/photo-tutor" 
                    element={
                      <ProtectedRoute requireUsage={true} requireSubscription={false}>
                        <Layout>
                          <PhotoTutorPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/problem/:id" 
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ProblemDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/account" 
                    element={
                      <ProtectedRoute requireSubscription={false}>
                        <Layout>
                          <AccountPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/quiz-setup" 
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <QuizSetupPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/quiz" 
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <QuizPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/paywall" 
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <PaywallPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/chat-tutor" 
                    element={
                      <ProtectedRoute requireUsage={true} requireSubscription={false}>
                        <Layout>
                          <ChatTutorPage />
                        </Layout>
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Catch all route */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
              
              <Toaster 
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#1f2937',
                    color: '#f9fafb',
                    border: '1px solid #374151',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                  },
                  success: {
                    iconTheme: {
                      primary: '#10b981',
                      secondary: '#1f2937',
                    },
                  },
                  error: {
                    iconTheme: {
                      primary: '#ef4444',
                      secondary: '#1f2937',
                    },
                  },
                }}
              />
            </div>
          </Router>
        </SubscriptionProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
